import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import MultiSelect from '../ui/multi-select';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import LoadingOverlay from '../ui/loading-overlay';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  X,
  Copy,
  CheckCircle,
  AlertCircle,
  Clock,
  CreditCard,
  Calendar,
  DollarSign,
  MapPin,
  User,
  Building,
  Hash,
  FileText,
  TrendingUp,
  Receipt,
  LayoutList,
  LayoutGrid
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useReference } from '../../contexts/ReferenceContext';
import { useToast } from '../../hooks/use-toast';
import { getCountryName, getCountryFlag } from '../../utils/countries';
import { getChannelIcon } from '../../utils/channelIcons';
import MerchantSearchSelect from '../ui/merchant-search-select';
import { 
  formatCurrencyForOtherScreens,
  formatNumber as formatNumberForOtherScreens,
  formatCurrencySelector,
  getCurrencyDisplay,
} from '../../utils/currencyFormatter';

// Helper function for currency formatting
const formatCurrency = (amount, currency = 'USD') => {
  return formatCurrencyForOtherScreens(amount, currency);
};

// Helper for formatting large numbers
const formatNumber = (number) => {
  return formatNumberForOtherScreens(number);
};

// Status badge component - matches TransactionHistory styling
const StatusBadge = ({ status }) => {
  // Show checkmark with "Completed" text for completed status with green background
  if (status === 'completed') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-200 capitalize">
        <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
        Completed
      </Badge>
    );
  }
  
  // Show "Pending" with gray background
  if (status === 'pending') {
    return (
      <Badge className="bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200 capitalize">
        Pending
      </Badge>
    );
  }
  
  // All other statuses show with light red background
  return (
    <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100 capitalize">
      {status}
    </Badge>
  );
};

// Result Code Badge - matches TransactionHistory styling
const ResultCodeBadge = ({ resultCode, resultCodes = [] }) => {
  // Determine if it's an approved result code
  const isApproved = resultCode?.includes('Approved') || resultCode?.includes('approved') || 
                    resultCode === '00' || resultCode === '10' || resultCode === '11';
  
  // Find the result code name from settings table
  const resultCodeInfo = resultCodes.find(rc => rc.code === resultCode || rc === resultCode);
  const description = typeof resultCodeInfo === 'object' 
    ? (resultCodeInfo?.name || resultCodeInfo?.description || '')
    : '';
  
  // Strip the code prefix if description starts with the code (handles "00-Approved" and "A-91-Sent to 3DS")
  const cleanDesc = description?.startsWith(resultCode)
    ? description.slice(resultCode.length).replace(/^[\s-]+/, '')
    : description;
  
  // Check for 3DS Declined or Risk Score Declined - use light orange/amber
  const lowerDesc = cleanDesc?.toLowerCase() || '';
  const is3DSDeclined = lowerDesc.includes('3ds') || lowerDesc.includes('3d secure') || lowerDesc.includes('3d_secure');
  const isRiskDeclined = lowerDesc.includes('risk');
  const isOrangeStatus = is3DSDeclined || isRiskDeclined;
  
  // Always show code: description
  const displayText = cleanDesc && cleanDesc !== resultCode 
    ? `${resultCode}: ${cleanDesc}` 
    : resultCode;
  
  if (isApproved) {
    return (
      <Badge className="text-xs font-medium bg-green-100 text-green-800 border-green-200 hover:bg-green-200">
        {displayText}
      </Badge>
    );
  } else if (isOrangeStatus) {
    return (
      <Badge 
        className="text-xs font-medium border hover:opacity-80"
        style={{ backgroundColor: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}
      >
        {displayText}
      </Badge>
    );
  } else {
    return (
      <Badge className="text-xs font-medium bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200">
        {displayText}
      </Badge>
    );
  }
};

// Payment Method Icon - matches TransactionHistory styling
const PaymentMethodIcon = ({ method, brand, paymentMethodLogos = {} }) => {
  const getCardBrandIcon = (cardBrand) => {
    const brandLower = cardBrand?.toLowerCase();
    
    switch (brandLower) {
      case 'visa':
        return (
          <img 
            src="/images/payment-methods/visa.png" 
            alt="Visa"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'mastercard':
      case 'master':
        return (
          <img 
            src="/images/payment-methods/mastercard.png" 
            alt="Mastercard"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'american express':
      case 'amex':
        return (
          <img 
            src="/images/payment-methods/amex.png" 
            alt="American Express"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'discover':
        return (
          <img 
            src="/images/payment-methods/discover.png" 
            alt="Discover"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'jcb':
        return (
          <CreditCard className="w-5 h-5 text-purple-600" />
        );
      case 'diners':
      case 'diners club':
        return (
          <CreditCard className="w-5 h-5 text-blue-700" />
        );
      default:
        return (
          <CreditCard className="w-5 h-5 text-gray-500" />
        );
    }
  };

  const getPaymentMethodIcon = (paymentMethod) => {
    const methodLower = paymentMethod?.toLowerCase();
    
    console.log('[PaymentMethodIcon] Method:', paymentMethod, 'MethodLower:', methodLower, 'Has logo:', !!paymentMethodLogos[methodLower], 'Logo URL:', paymentMethodLogos[methodLower]);
    
    // Use dynamically loaded logos from settings table
    if (paymentMethodLogos[methodLower]) {
      console.log('[PaymentMethodIcon] Showing logo for:', methodLower);
      return (
        <img 
          src={paymentMethodLogos[methodLower]} 
          alt={paymentMethod}
          className="h-7 w-auto object-contain"
          style={{ maxWidth: '50px' }}
          onError={(e) => {
            console.error('[PaymentMethodIcon] Image failed to load:', paymentMethodLogos[methodLower]);
            e.target.style.display = 'none';
          }}
        />
      );
    }
    
    console.log('[PaymentMethodIcon] No logo found, using fallback for:', methodLower);
    
    // Fallback badges for methods without logos
    switch (methodLower) {
      case 'google_pay':
      case 'google pay':
        return (
          <div className="w-8 h-5 bg-green-500 rounded text-white text-xs flex items-center justify-center font-bold">
            GP
          </div>
        );
      case 'bank_transfer':
        return (
          <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
            BT
          </div>
        );
      case 'crypto_wallet':
        return (
          <div className="w-8 h-5 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">
            CW
          </div>
        );
      default:
        return (
          <div className="w-8 h-5 bg-gradient-to-r from-blue-500 to-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">
            <CreditCard className="h-3 w-3" />
          </div>
        );
    }
  };

  return (
    <div className="flex items-center">
      {(method === 'credit_card' || method === 'card') && brand ? getCardBrandIcon(brand) : getPaymentMethodIcon(method)}
    </div>
  );
};

// Transaction Card Component
const TransactionCard = ({ transaction, onCardClick, paymentMethodLogos = {}, resultCodes = [] }) => {
  const statusColor = {
    completed: 'border-l-green-500',
    pending: 'border-l-gray-400',
    failed: 'border-l-red-500',
    cancelled: 'border-l-gray-500',
  }[transaction.status] || 'border-l-blue-500';

  // Approved is determined solely by result_code (00/10), matching the backend
  // definition. Handles plain codes and '-suffix' names (e.g. '10-Approved...').
  const isApproved = /^(00|10)($|-)/.test(transaction.result_code || '');
  
  // Check if amount is negative (void, refund, chargeback)
  const isNegativeAmount = transaction.amount < 0;

  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 ${statusColor} group`}
      onClick={() => onCardClick(transaction)}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header: Amount and Status */}
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className={`text-2xl font-bold ${isNegativeAmount ? 'text-red-600' : isApproved ? 'text-green-600' : 'text-gray-900'}`}>
              {formatCurrency(transaction.amount, transaction.currency)}
            </div>
            {/* Show USD equivalent if available and not USD */}
            {transaction.converted_amount && transaction.currency !== 'USD' && (
              <div className={`text-xs mt-1 ${isNegativeAmount ? 'text-red-400' : 'text-gray-500'}`}>
                ≈ {formatCurrency(transaction.converted_amount, 'USD')}
              </div>
            )}
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(transaction.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={transaction.status} />
            {transaction.result_code && (
              <ResultCodeBadge resultCode={transaction.result_code} resultCodes={resultCodes} />
            )}
          </div>
        </div>

        {/* Merchant Name */}
        {transaction.merchant_name && (
          <div className="flex items-center gap-2 text-sm border-b border-gray-100 pb-2">
            <Building className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-gray-700 text-xs truncate font-medium">{transaction.merchant_name}</span>
          </div>
        )}

        {/* Transaction Type and Channel */}
        {(transaction.transaction_type || transaction.channel) && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <div className="flex items-center gap-2">
              {transaction.transaction_type && (
                <span className="text-xs text-gray-600 capitalize">{transaction.transaction_type.replace(/_/g, ' ')}</span>
              )}
              {transaction.transaction_type && transaction.channel && (
                <span className="text-xs text-gray-400">•</span>
              )}
              {transaction.channel && (() => {
                const channelInfo = getChannelIcon(transaction.channel);
                const ChannelIcon = channelInfo.icon;
                return (
                  <div className="flex items-center gap-1">
                    <ChannelIcon className={`h-3.5 w-3.5 ${channelInfo.color}`} />
                    <span className="text-xs text-gray-500">{channelInfo.label}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Transaction ID, Reference and Description */}
        <div className="flex items-start gap-2 text-sm">
          <Hash className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-gray-600 truncate">
              {transaction.transaction_id}
            </div>
            {transaction.reference && (
              <div className="text-xs text-gray-500 truncate mt-0.5">
                Ref: {transaction.reference}
              </div>
            )}
            {transaction.description && (
              <div className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                {transaction.description}
              </div>
            )}
          </div>
        </div>

        {/* Customer Info */}
        {transaction.customer_name && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-gray-700 truncate font-medium">{transaction.customer_name}</span>
              {transaction.customer_email && (
                <span className="text-xs text-gray-500 truncate">{transaction.customer_email}</span>
              )}
            </div>
          </div>
        )}

        {/* Payment Method with Icon, Card Last 4 and Country Flag */}
        <div className="flex items-center gap-2 text-sm">
          {transaction.payment_method && (
            <div className="flex items-center gap-2 min-w-0">
              <PaymentMethodIcon method={transaction.payment_method} brand={transaction.card_brand} paymentMethodLogos={paymentMethodLogos} />
              {transaction.card_masked && (
                <span className="text-xs text-gray-600 font-mono">
                  •••• {transaction.card_masked.slice(-4)}
                </span>
              )}
              {transaction.card_country && (
                <>
                  <span 
                    className={`fi fi-${transaction.card_country.toLowerCase()}`}
                    style={{ fontSize: '0.875rem', width: '1rem', height: '0.75rem' }}
                    title={getCountryName(transaction.card_country)}
                  ></span>
                  <span className="text-xs text-gray-500">
                    {getCountryName(transaction.card_country)}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* View Details Button */}
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between text-blue-600 text-sm font-medium group-hover:text-blue-700">
            <span>View Full Receipt</span>
            <Receipt className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Receipt Detail Modal with 5 W's Categorization
const ReceiptModal = ({ transaction, isOpen, onClose, resultCodes = [] }) => {
  const { toast } = useToast();

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  if (!transaction) return null;

  // 5 W's Data Organization
  const whenData = [
    { icon: Calendar, label: 'Transaction Date', value: new Date(transaction.created_at).toLocaleString() },
    { icon: Clock, label: 'Last Updated', value: transaction.updated_at ? new Date(transaction.updated_at).toLocaleString() : 'N/A' },
  ];

  const whatData = [
    { icon: DollarSign, label: 'Amount', value: formatCurrency(transaction.amount, transaction.currency), highlight: true, isNegative: transaction.amount < 0 },
    { icon: DollarSign, label: 'Currency', value: transaction.currency },
    { icon: TrendingUp, label: 'Status', value: transaction.status, badge: true },
    { icon: FileText, label: 'Transaction Type', value: transaction.transaction_type || 'sale' },
    { icon: Hash, label: 'Transaction ID', value: transaction.transaction_id, copyable: true },
    { icon: Hash, label: 'Reference', value: transaction.reference },
    { icon: Hash, label: 'Bank Reference', value: transaction.bank_reference },
    { icon: FileText, label: 'Description', value: transaction.description },
  ];

  const whoData = [
    { icon: User, label: 'Customer Name', value: transaction.customer_name },
    { icon: CreditCard, label: 'Customer Email', value: transaction.customer_email, copyable: true },
    { icon: Building, label: 'Merchant', value: transaction.merchant_name },
    { icon: Building, label: 'Merchant Group', value: transaction.merchant_group },
    { icon: FileText, label: 'Platform', value: transaction.platform_name || transaction.platform_id },
  ];

  const whereData = [
    { icon: MapPin, label: 'Transaction Country', value: getCountryName(transaction.country) },
    { icon: MapPin, label: 'Customer Country', value: getCountryName(transaction.customer_country) },
    { icon: MapPin, label: 'Card Country', value: getCountryName(transaction.card_country) },
    { icon: Hash, label: 'IP Address', value: transaction.ip, copyable: true },
    { icon: MapPin, label: 'IP Country', value: getCountryName(transaction.ip_country) },
    { icon: FileText, label: 'Payment Processor', value: transaction.payment_processor },
    { icon: Building, label: 'Card Bank', value: transaction.card_bank },
  ];

  const whyData = [
    { icon: CreditCard, label: 'Payment Method', value: transaction.payment_method?.replace(/_/g, ' ') },
    { icon: CreditCard, label: 'Card Number', value: transaction.card_masked },
    { icon: CreditCard, label: 'Card Brand', value: transaction.card_brand },
    { icon: CreditCard, label: 'Card Type', value: transaction.card_type },
    { icon: AlertCircle, label: 'Risk Action', value: transaction.risk_action },
    { icon: Hash, label: 'Result Code', value: transaction.result_code, badge: true },
    { icon: Hash, label: 'Approval Code', value: transaction.approval_code },
  ];

  const DetailSection = ({ title, icon: Icon, data, color, emoji }) => {
    const hasData = data.some(item => item.value);
    if (!hasData) return null;

    return (
      <div className="mb-4">
        {/* Section Header - Simple */}
        <div className="flex items-center gap-2 mb-3 pb-2 border-b-2" style={{ borderColor: color }}>
          <span className="text-xl">{emoji}</span>
          <h3 className="text-sm font-semibold text-gray-700">
            {title.split(' - ')[1]}
          </h3>
        </div>

        {/* Section Content - Clean List */}
        <div className="space-y-2 pl-1">
          {data.map((row, index) => {
            if (!row.value) return null;

            const RowIcon = row.icon;
            return (
              <div 
                key={index}
                className="flex items-start justify-between gap-4 py-2 hover:bg-gray-50 rounded transition-colors"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <RowIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600 truncate">
                    {row.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {row.badge ? (
                    row.label === 'Result Code' ? (
                      <ResultCodeBadge resultCode={row.value} resultCodes={resultCodes} />
                    ) : (
                      <StatusBadge status={row.value} />
                    )
                  ) : (
                    <span className={`text-sm text-right font-medium ${row.highlight ? 'text-lg font-bold' : 'text-gray-900'} ${row.isNegative ? 'text-red-600' : ''}`} style={row.highlight && !row.isNegative ? { color } : {}}>
                      {row.value}
                    </span>
                  )}
                  {row.copyable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(row.value, row.label)}
                      title={`Copy ${row.label}`}
                    >
                      <Copy className="h-3 w-3 text-gray-400" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" />
            Transaction Receipt
          </DialogTitle>
        </DialogHeader>

        {/* Receipt Header - Simple Modern */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className={`text-3xl font-bold mb-2 ${transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {formatCurrency(transaction.amount, transaction.currency)}
            </div>
            <div className="flex items-center justify-center gap-2 mb-3">
              <StatusBadge status={transaction.status} />
              {transaction.result_code && (
                <ResultCodeBadge resultCode={transaction.result_code} resultCodes={resultCodes} />
              )}
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
              <Hash className="h-3 w-3" />
              <span className="font-mono">{transaction.transaction_id}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0"
                onClick={() => copyToClipboard(transaction.transaction_id, 'Transaction ID')}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* 5 W's Sections - Simple Modern */}
        <div className="space-y-6">
          <DetailSection 
            title="📅 WHEN - Temporal Information" 
            icon={Calendar}
            data={whenData}
            color="#3b82f6"
            emoji="📅"
          />

          <DetailSection 
            title="💰 WHAT - Transaction Details" 
            icon={DollarSign}
            data={whatData}
            color="#10b981"
            emoji="💰"
          />

          <DetailSection 
            title="👥 WHO - Parties Involved" 
            icon={User}
            data={whoData}
            color="#8b5cf6"
            emoji="👥"
          />

          <DetailSection 
            title="🌍 WHERE - Location & Geography" 
            icon={MapPin}
            data={whereData}
            color="#f59e0b"
            emoji="🌍"
          />

          <DetailSection 
            title="❓ WHY - Context & Risk Assessment" 
            icon={AlertCircle}
            data={whyData}
            color="#ef4444"
            emoji="❓"
          />
        </div>

        {/* Action Buttons - Simple */}
        <div className="mt-6 flex justify-between items-center gap-3 pt-4 border-t">
          <div className="text-xs text-gray-500">
            <Clock className="h-3 w-3 inline mr-1" />
            {new Date(transaction.updated_at || transaction.created_at).toLocaleString()}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={() => window.print()}>
              <Download className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const TransactionHistoryCards = () => {
  const { backendUrl, token, selectedPlatform, user } = useAuth();
  const reference = useReference();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // State
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(12); // Default cards per page
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Available filter options (from backend)


  // Fetch payment method logos from settings table
  const [paymentMethodLogos, setPaymentMethodLogos] = useState({});
  
  useEffect(() => {
    const fetchPaymentMethodLogos = async () => {
      if (!token) return;
      
      try {
        // Use public endpoint that works for all authenticated users
        const response = await axios.get(`${backendUrl}/api/settings/payment-methods/logos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPaymentMethodLogos(response.data.logos || {});
        console.log('[TransactionCards] Payment method logos loaded:', response.data.logos);
      } catch (error) {
        console.error('[TransactionCards] Error fetching payment method logos:', error);
      }
    };
    
    fetchPaymentMethodLogos();
  }, [token, backendUrl]);

  const [availableFilters, setAvailableFilters] = useState({
    countries: [],
    currencies: [],
    merchants: [],
    result_codes: [],
    statuses: [],
    channels: [],
  });

  // Table-backed filter lists (currencies/statuses/channels) come from the DB
  // Settings tables via ReferenceProvider. (Country stays data-driven.)
  useEffect(() => {
    setAvailableFilters(prev => ({
      ...prev,
      currencies: (reference.currencies || []).map(c => c.code),
      statuses: (reference.statuses || []).map(s => s.code),
      channels: reference.channels || [],
    }));
  }, [reference.currencies, reference.statuses, reference.channels]);

  // Read initial filter state from URL params (persisted from Grid view)
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    return {
      status: params.get('status') ? params.get('status').split(',') : [],
      country: params.get('country') || 'all',
      currency: params.get('currency') || 'all',
      merchant_id: params.get('merchant_id') || 'all',
      result_code: params.get('result_code') ? params.get('result_code').split(',') : [],
      channel: params.get('channel') ? params.get('channel').split(',') : [],
    };
  };

  const getInitialPeriod = () => {
    const params = new URLSearchParams(location.search);
    return {
      period: params.get('period') || 'today',
      customDays: params.get('customDays') ? params.get('customDays').split(',').map(Number) : [],
      customMonth: params.get('customMonth') ? parseInt(params.get('customMonth')) : new Date().getMonth() + 1,
      customYear: params.get('customYear') ? parseInt(params.get('customYear')) : new Date().getFullYear(),
    };
  };

  // Filters matching TransactionHistory
  const [filters, setFilters] = useState(getInitialFilters);

  // Period filter
  const [periodFilter, setPeriodFilter] = useState(getInitialPeriod);

  // Helper function to get number of days in a month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  // Build URL search params from current filter state
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (filters.status.length > 0) params.set('status', filters.status.join(','));
    if (filters.country && filters.country !== 'all') params.set('country', filters.country);
    if (filters.currency && filters.currency !== 'all') params.set('currency', filters.currency);
    if (filters.merchant_id && filters.merchant_id !== 'all') params.set('merchant_id', filters.merchant_id);
    if (filters.result_code.length > 0) params.set('result_code', filters.result_code.join(','));
    if (filters.channel.length > 0) params.set('channel', filters.channel.join(','));
    if (searchTerm) params.set('search', searchTerm.trim());
    params.set('period', periodFilter.period);
    if (periodFilter.period === 'custom') {
      params.set('customMonth', periodFilter.customMonth.toString());
      params.set('customYear', periodFilter.customYear.toString());
      if (periodFilter.customDays.length > 0) params.set('customDays', periodFilter.customDays.join(','));
    }
    return params.toString();
  };

  // Persist chosen Grid/Card view to backend (only if current default_screen is already a transaction view,
  // so we don't override an explicit Settings choice like Overview/Analytics/etc.)
  const persistViewPreference = async (targetScreen) => {
    try {
      const transactionScreens = ['/dashboard/transactions', '/dashboard/transactions-cards'];
      const currentDefault = user?.default_screen;
      if (currentDefault && transactionScreens.includes(currentDefault) && currentDefault !== targetScreen) {
        await axios.put(
          `${backendUrl}/api/users/me/default-screen`,
          { default_screen: targetScreen },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (e) {
      console.warn('[TransactionHistoryCards] Failed to persist view preference:', e);
    }
  };

  // Fetch available filters
  const fetchFilters = async () => {
    if (!token || !selectedPlatform) return;
    
    try {
      const API = `${backendUrl}/api`;
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };
      
      // Country list is data-driven; currencies/statuses/channels come from the tables.
      const [countriesRes, merchantsRes, resultCodesRes] = await Promise.all([
        axios.get(`${API}/filters/countries?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers }),
        axios.get(`${API}/filters/merchants?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers }),
        axios.get(`${API}/filters/result-codes?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers }),
      ]);

      setAvailableFilters(prev => ({
        ...prev,
        countries: countriesRes.data.countries || [],
        merchants: merchantsRes.data.merchants || [],
        result_codes: resultCodesRes.data.result_codes || [],
      }));
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    if (!token || !selectedPlatform) {
      setTransactions([]);
      setTotalCount(0);
      setTotalAmount(0);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
      });

      // Add platform_id only if it's not 'all'
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }

      // Add period filter
      if (periodFilter.period !== 'custom') {
        params.append('period', periodFilter.period);
      } else {
        params.append('period', 'custom');
        params.append('month', periodFilter.customMonth);
        params.append('year', periodFilter.customYear);
        // Add days filter if specific days are selected
        if (periodFilter.customDays && periodFilter.customDays.length > 0) {
          params.append('days', periodFilter.customDays.join(','));
        }
      }

      // Add search
      if (searchTerm) params.append('search', searchTerm.trim());
      
      // Add status filter (MultiSelect)
      if (filters.status && filters.status.length > 0) {
        filters.status.forEach(status => params.append('status', status));
      }
      



      // Add other filters
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.currency && filters.currency !== 'all') params.append('currency', filters.currency);
      if (filters.merchant_id && filters.merchant_id !== 'all') params.append('merchant_id', filters.merchant_id);
      
      // Add channel filter
      if (filters.channel && filters.channel.length > 0) {
        filters.channel.forEach(ch => params.append('channel', ch));
      }
      
      // Add result code filter
      if (filters.result_code && filters.result_code.length > 0) {
        filters.result_code.forEach(code => params.append('result_code', code));
      }

      const response = await axios.get(
        `${backendUrl}/api/transactions?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTransactions(response.data.transactions);
      setTotalCount(response.data.total);
      
      // Fetch total amount
      const totalParams = new URLSearchParams();
      if (selectedPlatform && selectedPlatform !== 'all') {
        totalParams.append('platform_id', selectedPlatform);
      }
      if (periodFilter.period !== 'custom') {
        totalParams.append('period', periodFilter.period);
      } else {
        totalParams.append('period', 'custom');
        totalParams.append('month', periodFilter.customMonth);
        totalParams.append('year', periodFilter.customYear);
        // Add days filter if specific days are selected
        if (periodFilter.customDays && periodFilter.customDays.length > 0) {
          totalParams.append('days', periodFilter.customDays.join(','));
        }
      }
      if (searchTerm) totalParams.append('search', searchTerm.trim());
      if (filters.status && filters.status.length > 0) {
        filters.status.forEach(status => totalParams.append('status', status));
      }
      if (filters.country && filters.country !== 'all') totalParams.append('country', filters.country);
      if (filters.currency && filters.currency !== 'all') totalParams.append('currency', filters.currency);
      if (filters.merchant_id && filters.merchant_id !== 'all') totalParams.append('merchant_id', filters.merchant_id);
      if (filters.channel && filters.channel.length > 0) {
        filters.channel.forEach(ch => totalParams.append('channel', ch));
      }
      if (filters.result_code && filters.result_code.length > 0) {
        filters.result_code.forEach(code => totalParams.append('result_code', code));
      }

      const totalResponse = await axios.get(
        `${backendUrl}/api/transactions/totals?${totalParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTotalAmount(totalResponse.data.total_amount || 0);

    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch transactions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions when platform changes
  useEffect(() => {
    if (token && selectedPlatform) {
      fetchTransactions();
    }
  }, [selectedPlatform]);
  
  // Fetch filters AFTER initial load
  useEffect(() => {
    if (token && selectedPlatform && !loading) {
      fetchFilters();
    }
  }, [loading]);

  // Debounced search - triggers fetch after 500ms of no typing
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (currentPage === 1) {
        fetchTransactions();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Fetch transactions when filters change
  useEffect(() => {
    if (token && selectedPlatform) {
      fetchTransactions();
    }
  }, [currentPage, perPage, filters, periodFilter]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Handle card click
  const handleCardClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowReceiptModal(true);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / perPage);

  // Calculate active filters count
  const activeFiltersCount = 
    (filters.status?.length || 0) +
    (filters.channel?.length || 0) +
    (filters.country && filters.country !== 'all' ? 1 : 0) +
    (filters.currency && filters.currency !== 'all' ? 1 : 0) +
    (filters.merchant_id && filters.merchant_id !== 'all' ? 1 : 0) +
    (filters.result_code?.length || 0) +
    (searchTerm ? 1 : 0);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
              <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-500" />
              Transaction Cards
            </h1>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-sm">
              Mobile-friendly card view of all transactions
            </p>
          </div>
          {/* View Toggle - positioned next to title */}
          <div className="flex items-center gap-1 bg-muted rounded-xl p-1.5" data-testid="view-toggle">
            <button
              onClick={() => { sessionStorage.setItem('txn-view-manual', '1'); const q = buildFilterParams(); persistViewPreference('/dashboard/transactions'); navigate(`/dashboard/transactions${q ? '?' + q : ''}`); }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all text-muted-foreground hover:text-foreground"
              data-testid="view-toggle-grid"
              title="Table View"
            >
              <LayoutList className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400" />
              Grid
            </button>
            <button
              onClick={() => { persistViewPreference('/dashboard/transactions-cards'); navigate('/dashboard/transactions-cards'); }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all bg-background shadow-sm text-foreground"
              data-testid="view-toggle-card"
              title="Card View"
            >
              <LayoutGrid className="h-4 w-4 lg:h-5 lg:w-5 text-blue-500" />
              Card
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Summary Card */}
          <Card className="w-auto sm:w-56 shrink-0">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <CreditCard className="h-4 w-4" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4 px-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="text-base font-semibold" style={{ color: 'var(--input-text-color)' }}>{formatNumber(totalCount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total:</span>
                <span className="text-base font-semibold" style={{ color: 'var(--input-text-color)' }}>
                  {formatCurrency(
                    totalAmount, 
                    filters.currency && filters.currency !== 'all' 
                      ? filters.currency 
                      : 'USD'
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          {/* Filter Section - Matching TransactionHistory layout */}
          <div className="space-y-4">
            {/* Row 1: Period and Search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              {/* Period Filter - First Position */}
              <div className="flex items-center gap-2">
                <Select value={periodFilter.period} onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, period: value }))}>
                  <SelectTrigger className="w-36">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="last7days">Last 7 Days</SelectItem>
                    <SelectItem value="thismonth">This Month</SelectItem>
                    <SelectItem value="thisyear">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom Month/Year Selectors */}
                {periodFilter.period === 'custom' && (
                  <>
                    {/* Days Multi-Select */}
                    <MultiSelect
                      options={['All Days', ...Array.from({ length: getDaysInMonth(periodFilter.customMonth, periodFilter.customYear) }, (_, i) => (i + 1).toString())]}
                      value={periodFilter.customDays.length === 0 ? ['All Days'] : periodFilter.customDays.map(d => d.toString())}
                      onChange={(value) => {
                        if (value.includes('All Days') && value.length > 1) {
                          if (periodFilter.customDays.length > 0) {
                            setPeriodFilter(prev => ({ ...prev, customDays: [] }));
                          } else {
                            setPeriodFilter(prev => ({ ...prev, customDays: value.filter(v => v !== 'All Days').map(d => parseInt(d)) }));
                          }
                        } else if (value.includes('All Days') && value.length === 1) {
                          setPeriodFilter(prev => ({ ...prev, customDays: [] }));
                        } else {
                          setPeriodFilter(prev => ({ ...prev, customDays: value.map(d => parseInt(d)) }));
                        }
                      }}
                      placeholder="All Days"
                      className="w-32"
                      maxDisplayItems={1}
                      renderOption={(day) => day}
                      renderValue={(day) => day === 'All Days' ? 'All Days' : `Day ${day}`}
                    />
                    
                    <Select 
                      value={periodFilter.customMonth.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customMonth: parseInt(value), customDays: [] }))}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select 
                      value={periodFilter.customYear.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customYear: parseInt(value), customDays: [] }))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 5 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              
              {/* Search Box */}
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground/50" />
                  <Input
                    placeholder="Search exact value: ID, email, name, card…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full"
                    data-testid="transaction-search-input-cards"
                  />
                </div>
                <p className="mt-1 text-xs text-foreground/50" data-testid="transaction-search-hint-cards">
                  Exact match — enter the full value
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
                {activeFiltersCount > 0 && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setFilters({
                      status: [],
                      country: 'all',
                      currency: 'all',
                      merchant_id: 'all',
                      result_code: [],
                      channel: [],
                    });
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                
                <Button onClick={fetchTransactions} variant="outline" size="sm" disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>

                {/* Cards Per Page */}
                <Select value={perPage.toString()} onValueChange={(val) => {
                  setPerPage(parseInt(val));
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 per page</SelectItem>
                    <SelectItem value="12">12 per page</SelectItem>
                    <SelectItem value="24">24 per page</SelectItem>
                    <SelectItem value="48">48 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 2: Data Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Status Filter */}
              <MultiSelect
                options={[
                  'All', 
                  ...[...new Set(availableFilters.statuses)].sort((a, b) => a.localeCompare(b))
                ]}
                value={filters.status}
                onChange={(value) => {
                  if (value.includes('All')) {
                    handleFilterChange('status', []);
                  } else {
                    handleFilterChange('status', value);
                  }
                }}
                placeholder="All Status"
                className="w-40"
                maxDisplayItems={1}
                renderOption={(status) => {
                  if (status === 'All') return 'All Status';
                  return status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ');
                }}
                renderValue={(status) => {
                  if (status === 'All') return 'All Status';
                  return status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ');
                }}
              />
              
              {/* Channel Filter */}
              <MultiSelect
                options={[
                  { code: 'All', name: 'All Channels' },
                  ...(availableFilters.channels || [])
                    .slice()
                    .sort((a, b) => (a.name || a.code || '').localeCompare(b.name || b.code || ''))
                ]}
                value={filters.channel}
                onChange={(value) => {
                  if (value.includes('All')) {
                    handleFilterChange('channel', []);
                  } else {
                    handleFilterChange('channel', value);
                  }
                }}
                placeholder="All Channels"
                className="w-40"
                maxDisplayItems={1}
                renderOption={(channel) => {
                  if (channel.code === 'All') return 'All Channels';
                  return channel.name || channel.code;
                }}
                renderValue={(channel) => {
                  if (channel.code === 'All') return 'All Channels';
                  return channel.name || channel.code;
                }}
              />

              {/* Merchant Filter */}
              <MerchantSearchSelect
                merchants={availableFilters.merchants}
                value={filters.merchant_id || "all"}
                onChange={(value) => handleFilterChange('merchant_id', value === "all" ? '' : value)}
                placeholder="All Merchants"
                className="w-44"
              />
              
              {/* Country Filter */}
              <Select value={filters.country || "all"} onValueChange={(value) => handleFilterChange('country', value === "all" ? '' : value)}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {availableFilters.countries
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Currency Filter */}
              <Select value={filters.currency || "all"} onValueChange={(value) => handleFilterChange('currency', value === "all" ? '' : value)}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All">
                    {filters.currency && filters.currency !== 'all' 
                      ? getCurrencyDisplay(filters.currency)
                      : "All Currencies"
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  {availableFilters.currencies
                    .slice()
                    .sort((a, b) => a.localeCompare(b))
                    .map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {formatCurrencySelector(currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Result Code Filter */}
              <MultiSelect
                options={[
                  { code: 'All', name: 'All Results' },
                  ...(availableFilters.result_codes || [])
                    .filter((item, index, self) => 
                      index === self.findIndex(t => t.code === item.code)
                    )
                    .filter(item => item.code && item.code !== '-' && item.code !== '0.0')
                    .sort((a, b) => a.code.localeCompare(b.code))
                ]}
                value={filters.result_code}
                onChange={(value) => {
                  if (value.includes('All')) {
                    handleFilterChange('result_code', []);
                  } else {
                    handleFilterChange('result_code', value);
                  }
                }}
                placeholder="All Results"
                className="w-44"
                maxDisplayItems={1}
                renderOption={(resultCode) => {
                  if (resultCode.code === 'All') return 'All Results';
                  const displayName = resultCode.name || resultCode.code;
                  const cleanName = displayName.startsWith(resultCode.code)
                    ? displayName.slice(resultCode.code.length).replace(/^[\s-]+/, '')
                    : displayName;
                  return cleanName && cleanName !== resultCode.code
                    ? `${resultCode.code}: ${cleanName}`
                    : resultCode.code;
                }}
                renderValue={(resultCode) => {
                  if (resultCode.code === 'All') return 'All Results';
                  const displayName = resultCode.name || resultCode.code;
                  const cleanName = displayName.startsWith(resultCode.code)
                    ? displayName.slice(resultCode.code.length).replace(/^[\s-]+/, '')
                    : displayName;
                  return cleanName && cleanName !== resultCode.code
                    ? `${resultCode.code}: ${cleanName}`
                    : resultCode.code;
                }}
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Cards Grid */}
      {!selectedPlatform ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="h-12 w-12 text-blue-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Please select a platform</h3>
            <p className="text-gray-500">Choose a platform from the dropdown above to view transactions</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <LoadingOverlay message="Loading transactions..." />
      ) : transactions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your filters or search query</p>
            <div className="text-sm text-gray-400">
              <p>Current filters:</p>
              <p>Period: {periodFilter.period === 'custom' ? `${periodFilter.customMonth}/${periodFilter.customYear}` : periodFilter.period}</p>
              {filters.status?.length > 0 && <p>Status: {filters.status.join(', ')}</p>}
              {filters.country && <p>Country: {filters.country}</p>}
              {filters.currency !== 'all' && <p>Currency: {filters.currency}</p>}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {transactions.map((transaction) => (
            <TransactionCard
              key={transaction.id}
              transaction={transaction}
              onCardClick={handleCardClick}
              paymentMethodLogos={paymentMethodLogos}
              resultCodes={availableFilters.result_codes}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalCount)} of {formatNumber(totalCount)} transactions
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={i}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipt Modal */}
      <ReceiptModal
        transaction={selectedTransaction}
        isOpen={showReceiptModal}
        onClose={() => {
          setShowReceiptModal(false);
          setSelectedTransaction(null);
        }}
        resultCodes={availableFilters.resultCodes}
      />
    </div>
  );
};

export default TransactionHistoryCards;
